import { UNPROTECTED_PATHS } from '../navigation/paths';

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;
let isProbing = false;

/**
 * Lets the AuthProvider expose its `logout` to the axios interceptors,
 * which live outside of React and cannot use hooks.
 */
export const registerUnauthorizedHandler = (handler: UnauthorizedHandler) => {
  unauthorizedHandler = handler;
  return () => {
    if (unauthorizedHandler === handler) {
      unauthorizedHandler = null;
    }
  };
};

/**
 * Called when a request turns out to be unauthenticated: the backend invalidates
 * the oldest session when the same account is opened more than 5 times
 * concurrently, so the only way out is to log out and sign in again.
 */
export const handleUnauthorized = () => {
  // Nothing to log out from (e.g. a failed login attempt).
  if (!localStorage.getItem('token')) {
    return;
  }

  if (unauthorizedHandler) {
    unauthorizedHandler();
    return;
  }

  // No provider mounted yet: clear the session by hand and go to the login page.
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.replace(UNPROTECTED_PATHS.login);
};

const absoluteUrl = (config: any): string | null => {
  const url: string | undefined = config?.url;
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const baseURL: string | undefined = config?.baseURL;
  if (!baseURL) return null;
  return `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
};

/**
 * The API only sends CORS headers on 2xx responses, so the browser drops the
 * body *and* the status of a 401 and axios only reports a network error — the
 * expired session is invisible to us. A preflight-style OPTIONS request to the
 * same URL does come back with CORS headers, so if it succeeds we know the
 * server is up and the request really did fail on an error status: treat it as
 * the dropped session and log out.
 *
 * Remove this once the backend sends `Access-Control-Allow-Origin` on error
 * responses too — `handleUnauthorized` on a real 401 is then enough.
 */
const probeHiddenUnauthorized = async (config: any) => {
  if (isProbing) return;

  const url = absoluteUrl(config);
  if (!url) return;

  // Plainly offline: not a session problem.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  isProbing = true;
  try {
    const response = await fetch(url, { method: 'OPTIONS' });
    if (response.ok) {
      handleUnauthorized();
    }
  } catch {
    // The server is unreachable: a genuine network failure, keep the session.
  } finally {
    isProbing = false;
  }
};

/**
 * Single entry point for the axios response error interceptors.
 */
export const handleRequestError = (error: any) => {
  if (error?.response) {
    if (error.response.status === 401) {
      handleUnauthorized();
    }
    return;
  }

  // Aborted by us, not by the network.
  if (error?.code === 'ERR_CANCELED') return;

  // No response at all: could be the CORS-hidden 401, could be the network.
  if (!localStorage.getItem('token')) return;
  void probeHiddenUnauthorized(error?.config);
};
